import type { DayName, WorkingHour } from '@iheartjane/types';
import { addDays, format, isAfter, isSameDay, isValid, set } from 'date-fns';

import { timeIsBetween } from './timeIsBetween';

export interface StoreScheduleDay {
  closingTime: Date | null;
  dayName: DayName;
  formattedClosingTime: string | null; // e.g. 8:30 pm
  formattedOpeningTime: string | null; // e.g. 9:00 am
  isClosedAllDay: boolean;
  isOpenPartOfTheDay: boolean;
  isTimeDuringDay(time?: Date): boolean;
  openingTime: Date | null;
  willOpenLater(time?: Date): boolean;
}

/**
 * Returns a StoreScheduleDay if the day has no hours defined that day
 */
export function closedScheduleDay(dayName: DayName): StoreScheduleDay {
  return {
    closingTime: null,
    formattedClosingTime: null,
    formattedOpeningTime: null,
    isClosedAllDay: true,
    isOpenPartOfTheDay: false,
    dayName,
    openingTime: null,
    isTimeDuringDay() {
      return false;
    },
    willOpenLater() {
      return false;
    },
  };
}

/**
 * Returns the closing time of the store, or adds a day if the closing time is before the opening time
 */
export const setClosingTime = (openingTime: Date, closingTime: Date): Date => {
  if (isAfter(openingTime, closingTime)) {
    return addDays(closingTime, 1);
  }

  return closingTime;
};

/**
 * Returns a StoreScheduleDay if the day has an opening and closing time defined
 */
export function openScheduleDay({
  closingTime,
  dayName,
  openingTime,
}: {
  closingTime: Date;
  dayName: DayName;
  openingTime: Date;
}): StoreScheduleDay {
  const adjustedClosingTime = setClosingTime(openingTime, closingTime);

  const isTimeDuringDay = (time: Date) => {
    return timeIsBetween(time, openingTime, adjustedClosingTime);
  };

  // openingTime comes first so that a TZDate's zone decides which day it is
  const willOpenLater = (time: Date) => {
    return isAfter(openingTime, time) && isSameDay(openingTime, time);
  };

  return {
    closingTime: adjustedClosingTime,
    dayName,
    formattedClosingTime: isValid(closingTime)
      ? format(closingTime, 'h:mm aaa')
      : null,
    formattedOpeningTime: isValid(openingTime)
      ? format(openingTime, 'h:mm aaa')
      : null,
    isClosedAllDay: false,
    isOpenPartOfTheDay: true,
    openingTime,
    isTimeDuringDay,
    willOpenLater,
  };
}

/**
 * Converts a time string into a Date relative to a starting date
 *
 * This utility function takes a starting day reference point, a number of days to offset,
 * and a time string in "HH:MM" format, and returns a precise Date. Pass a TZDate as the
 * starting day to set the time in that zone; the result is a TZDate in the same zone.
 */
export const getScheduleTime = (
  startingDay: Date,
  daysToAdd: number,
  time: string
): Date | null => {
  const [hour, min] = time.split(':');
  if (!(hour && min)) return null;
  return set(addDays(startingDay, daysToAdd), {
    hours: parseInt(hour, 10),
    minutes: parseInt(min, 10),
    seconds: 0,
    milliseconds: 0,
  });
};

/**
 * Creates a StoreScheduleDay object based on opening and closing times
 */
export function buildScheduleDay({
  closingTime,
  dayName,
  openingTime,
}: {
  closingTime: Date | null;
  dayName: DayName;
  openingTime: Date | null;
}): StoreScheduleDay {
  return openingTime && closingTime
    ? openScheduleDay({
        closingTime,
        dayName,
        openingTime,
      })
    : closedScheduleDay(dayName);
}

/**
 * Creates an extended 8-day schedule starting from yesterday
 *
 * This function generates an array of StoreScheduleDay objects covering 8 consecutive days
 * starting from yesterday. The 8-day format (rather than 7) is constructed by duplicating
 * yesterday at the end of the array, ensuring a complete view of the week ahead with context
 * of the previous day.
 *
 * The function:
 * 1. Identifies yesterday's position in the working hours array
 * 2. Reorders the days so that yesterday appears first, followed by the rest of the week
 * 3. Converts each WorkingHour entry into a StoreScheduleDay object with proper date/time values
 * 4. Handles both open days (with time ranges) and closed days (without times)
 *
 * This extended schedule format is useful for operations that need to compare across days
 * or handle overnight operations that span from one day to another.
 * @param params.workingHours - Array of WorkingHour objects from the store configuration
 * @param params.yesterday - Yesterday's date, as a TZDate in the store's timezone
 * @returns An array of 8 StoreScheduleDay objects starting from yesterday
 */
export function buildExtendedScheduleDays({
  workingHours,
  yesterday,
}: {
  workingHours: WorkingHour[];
  yesterday: Date;
}): StoreScheduleDay[] {
  const yesterdayName = format(yesterday, 'EEEE').toLowerCase();
  const yesterdayIndex = workingHours.findIndex(
    (day) => day.day === yesterdayName
  );
  const beforeDays = workingHours.slice(0, yesterdayIndex + 1);
  const restOfWeek = workingHours.slice(yesterdayIndex, workingHours.length);

  return restOfWeek.concat(beforeDays).map((day, index) => {
    const {
      day: name,
      period: { from, to },
    } = day;

    const openingTime = from ? getScheduleTime(yesterday, index, from) : null;
    const closingTime = to ? getScheduleTime(yesterday, index, to) : null;

    return buildScheduleDay({
      closingTime,
      dayName: name,
      openingTime,
    });
  });
}
